import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { getCartManager } from '@theme/cart-manager';

/**
 * Option produit de la fiche produit (bloc `ax-product-upsell`).
 *
 * Le choix Avec/Sans ne touche pas au panier : il est lu au moment où le produit
 * de la page part au panier, et l'option suit dans une seconde requête. Écouter
 * l'ajout du parent plutôt qu'intercepter son formulaire évite de toucher à
 * `product-form.js` — loader, sticky et quick-add restent intacts.
 *
 * L'option est un article PAYANT : la ligne part au prix catalogue. Une remise
 * éventuelle est configurée en admin (remise automatique), jamais par le thème,
 * qui n'envoie donc aucun montant. `_upsell_parent` porte l'id du produit de la
 * page et sert au retrait automatique (`component-cart-items.js`) ; il n'est posé
 * que si le marchand a coché l'option correspondante.
 *
 * @typedef {Object} AxProductUpsellRefs
 * @property {HTMLInputElement} withInput - Le radio « Avec ».
 * @property {HTMLParagraphElement} [error] - La zone de message d'erreur.
 *
 * @extends {Component<AxProductUpsellRefs>}
 */
class AxProductUpsell extends Component {
  requiredRefs = ['withInput'];

  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  get variantId() {
    return Number(this.dataset.variantId);
  }

  get parentProductId() {
    return String(this.dataset.productId);
  }

  /** Nom de la propriété de ligne, réglable dans l'éditeur. */
  get lineProperty() {
    return this.dataset.lineProperty || 'Upsell_shaker';
  }

  get isWanted() {
    return this.refs.withInput.checked;
  }

  /**
   * `CartAddEvent` partage son nom d'event avec `cart:update` : on ne retient que
   * les ajouts réussis venant du formulaire produit de CETTE fiche.
   * @param {CustomEvent & { detail: { data?: Record<string, unknown> } }} event
   */
  #onCartUpdate = (event) => {
    const data = event.detail?.data;
    if (!data || data.didError) return;
    if (data.source !== 'product-form-component') return;
    if (String(data.productId) !== this.parentProductId) return;

    this.#addUpsell();
  };

  async #addUpsell() {
    // Jamais d'écriture dans l'éditeur : on ne modifie pas le panier de test du
    // marchand pendant qu'il règle son bloc.
    if (this.#busy || Shopify.designMode) return;
    if (!this.isWanted || !this.variantId) return;

    this.#busy = true;

    try {
      this.#clearError();

      // Le panier réel, et non `data-in-cart` du rendu serveur : la fiche produit
      // n'est pas re-rendue après un ajout, son verdict est donc périmé dès le
      // deuxième passage — et une seconde ligne offerte arriverait FACTURÉE, une
      // seule opération de remise étant retenue par Shopify.
      //
      // `cartJSON` suffit : l'ajout du parent portait `sections`, donc le
      // cart-manager l'a rafraîchi AVANT de rendre la main au formulaire produit.
      // Un `getCart()` ici ne ferait que rejouer le même aller-retour.
      const cart = getCartManager().cartJSON ?? (await getCartManager().getCart());
      if (this.#upsellLines(cart).length) return;

      const payload = await this.#add();
      this.dataset.inCart = 'true';

      // Même arbitrage que `product-form.js` : on publie immédiatement avec le HTML
      // de section renvoyé par l'ajout, au lieu d'attendre un `/cart.js` de plus.
      // Sans `sections`, le drawer devait re-demander son rendu — d'où le délai
      // avant que la ligne offerte n'apparaisse.
      const updated = getCartManager().cartJSON;
      document.dispatchEvent(
        // `itemCount` est obligatoire : `cart-icon.js` le lit pour la pastille du
        // header et retombe à 0 en son absence.
        new CartUpdateEvent(updated, 'ax-product-upsell', {
          source: 'ax-product-upsell',
          itemCount: updated?.item_count ?? 0,
          sections: payload?.sections,
        })
      );
    } catch (error) {
      // Rupture de stock, limite de quantité : l'option ne doit jamais empêcher
      // l'achat du produit principal, déjà au panier à ce stade.
      this.#showError(error.message);
    } finally {
      this.#busy = false;
    }
  }

  /**
   * La ligne offerte se reconnaît à ses PROPRIÉTÉS : le client peut très bien
   * avoir acheté le même produit par ailleurs, au prix fort.
   * @param {{ items?: Array<{ id: number, properties?: Record<string, string> }> }} cart
   */
  #upsellLines(cart) {
    return (
      cart?.items?.filter((item) => item.id === this.variantId && item.properties?.[this.lineProperty]) ?? []
    );
  }

  /**
   * `sections` dans le body signale au cart-manager une requête du thème : il
   * rafraîchit `cartJSON` sans émettre d'event, et c'est nous qui émettons le
   * nôtre — sinon l'event partirait en double (cf. sidecart.md). La clé doit être
   * omise faute d'identifiant : Shopify répond 400 sur une chaîne vide.
   */
  get #sectionsPayload() {
    const section = document.querySelector('cart-drawer-component')?.closest('.shopify-section');
    if (!section?.id) return {};

    return { sections: section.id.replace('shopify-section-', '') };
  }

  async #add() {
    const body = JSON.stringify({
      items: [
        {
          id: this.variantId,
          quantity: 1,
          properties: {
            [this.lineProperty]: 'true',
            // `_upsell` masque le sélecteur de quantité côté panier, sans empêcher
            // le retrait. Posé uniquement en remise forcée : la remise ne porte que
            // sur une unité, un deuxième exemplaire partirait au prix fort. Au prix
            // catalogue, l'option est un article ordinaire, quantité comprise.
            ...(this.dataset.lockQuantity === 'true' ? { _upsell: 'true' } : {}),
            // Posé seulement si le retrait lié est activé — c'est sa présence qui
            // déclenche le nettoyage dans `component-cart-items`.
            ...(this.dataset.removeWithParent === 'true' ? { _upsell_parent: this.parentProductId } : {}),
          },
        },
      ],
      ...this.#sectionsPayload,
    });

    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(payload?.description || payload?.message || `Ajout impossible (HTTP ${response.status})`);
    }

    return payload;
  }

  /** @param {string} message */
  #showError(message) {
    const { error } = this.refs;
    if (!error) {
      console.error('Option offerte :', message);
      return;
    }

    error.textContent = message;
    error.hidden = false;
  }

  #clearError() {
    const { error } = this.refs;
    if (!error) return;

    error.textContent = '';
    error.hidden = true;
  }
}

if (!customElements.get('ax-product-upsell')) {
  customElements.define('ax-product-upsell', AxProductUpsell);
}
